import {
  approveIdea,
  type CardFieldsUpdate,
  declineIdea,
  rejectContent,
  setContentArchived,
  setPlaced,
  unrejectContent,
  updateCardFields,
} from '../api.ts';
import { describeError } from '../data.tsx';
import * as wording from '../wording.ts';
import { startProductionOf } from './actions.ts';
import { type BoardCard, cardTitle } from './model.ts';
import { skipMarkPosted, skipReject, skipUnreject } from './qualify.ts';

/**
 * Several cards at once (US-028, Design reference § Bulk actions): which
 * cards are selected, which of them an action applies to, and the run that
 * does it one card at a time. Pure, apart from the calls an action makes.
 */

/** A card toggled in or out. */
export function toggleSelected(selected: ReadonlySet<string>, cardId: string): ReadonlySet<string> {
  const next = new Set(selected);
  if (!next.delete(cardId)) next.add(cardId);
  return next;
}

/**
 * Shift+click: every card from the anchor to the clicked one in the column
 * (as drawn, top to bottom), both included. `null` when the anchor is not in
 * that column, and the click is a plain toggle then.
 */
export function rangeInColumn(column: readonly string[], anchorId: string | null, cardId: string): string[] | null {
  if (anchorId === null) return null;
  const from = column.indexOf(anchorId);
  const to = column.indexOf(cardId);
  if (from === -1 || to === -1) return null;
  return column.slice(Math.min(from, to), Math.max(from, to) + 1);
}

/** The selection without the cards that left the board; the same set when none did. */
export function pruneSelection(selected: ReadonlySet<string>, cards: readonly BoardCard[]): ReadonlySet<string> {
  const present = new Set(cards.map((card) => card.cardId));
  const kept = [...selected].filter((cardId) => present.has(cardId));
  return kept.length === selected.size ? selected : new Set(kept);
}

export type BulkActionId =
  | 'campaign'
  | 'due'
  | 'block'
  | 'unblock'
  | 'approve'
  | 'decline'
  | 'start'
  | 'posted'
  | 'reject'
  | 'unreject'
  | 'archive';

/**
 * What the actions that ask first were given: a campaign, a date, a blocked
 * reason, or the one optional rejection reason of **Reject…**.
 */
export type BulkInput = CardFieldsUpdate & { readonly rejectionReason?: string };

export interface BulkAction {
  readonly id: BulkActionId;
  readonly label: string;
  /** It asks for a campaign, a date or a reason before it runs. */
  readonly asks: boolean;
  /** Why the card is left out, or `null` when the action applies to it. */
  readonly skip: (card: BoardCard) => string | null;
  /** Does it to one card; a throw is a skip, with the server's reason. */
  readonly run: (card: BoardCard, input: BulkInput) => Promise<unknown>;
}

/** **Approve ideas** approves at most this many per run; the rest stay selected. */
export const APPROVE_IDEAS_MAX = 10;

const anyCard = (): null => null;
const setFields = (card: BoardCard, input: BulkInput): Promise<unknown> => updateCardFields(card.cardId, input);

function blockedReasonOf(card: BoardCard): string | null {
  return card.type === 'content' ? card.content.blockedReason : card.idea.blockedReason;
}

function draftedIdea(card: BoardCard): string | null {
  return card.type === 'idea' && card.idea.status === 'drafted' ? null : wording.BULK_SKIP_NOT_DRAFTED;
}

/** The actions, in the order the bar shows them (Clear selection is the bar's own). */
export const BULK_ACTIONS: readonly BulkAction[] = [
  { id: 'campaign', label: wording.BULK_SET_CAMPAIGN, asks: true, skip: anyCard, run: setFields },
  { id: 'due', label: wording.BULK_SET_DUE, asks: true, skip: anyCard, run: setFields },
  { id: 'block', label: wording.BULK_BLOCK, asks: true, skip: anyCard, run: setFields },
  {
    id: 'unblock',
    label: wording.BULK_UNBLOCK,
    asks: false,
    skip: (card) => (blockedReasonOf(card) === null ? wording.BULK_SKIP_NOT_BLOCKED : null),
    run: (card) => updateCardFields(card.cardId, { blockedReason: null }),
  },
  {
    id: 'approve',
    label: wording.BULK_APPROVE_IDEAS,
    asks: false,
    skip: draftedIdea,
    run: (card) => (card.type === 'idea' ? approveIdea(card.batch.id, card.idea.id) : Promise.resolve()),
  },
  {
    id: 'decline',
    label: wording.BULK_DECLINE_IDEAS,
    asks: false,
    skip: draftedIdea,
    run: (card) => (card.type === 'idea' ? declineIdea(card.batch.id, card.idea.id) : Promise.resolve()),
  },
  {
    id: 'start',
    label: wording.BULK_START_PRODUCTION,
    asks: false,
    skip: (card) => {
      if (card.type !== 'content' || card.content.status !== 'ready') return wording.BULK_SKIP_NOT_READY;
      return card.content.queuePosition === null ? null : wording.BULK_SKIP_QUEUED;
    },
    run: (card) =>
      card.type === 'content' ? startProductionOf(card.content.id, card.content.name) : Promise.resolve(),
  },
  {
    id: 'posted',
    label: wording.BULK_MARK_POSTED,
    asks: false,
    skip: skipMarkPosted,
    run: (card) => (card.type === 'content' ? setPlaced(card.content.id, true) : Promise.resolve()),
  },
  {
    id: 'reject',
    label: wording.BULK_REJECT,
    asks: true,
    skip: skipReject,
    run: (card, input) =>
      card.type === 'content' ? rejectContent(card.content.id, input.rejectionReason ?? '') : Promise.resolve(),
  },
  {
    id: 'unreject',
    label: wording.BULK_UNREJECT,
    asks: false,
    skip: skipUnreject,
    run: (card) => (card.type === 'content' ? unrejectContent(card.content.id) : Promise.resolve()),
  },
  {
    id: 'archive',
    label: wording.BULK_ARCHIVE,
    asks: false,
    skip: (card) => {
      if (card.type !== 'content') return wording.BULK_SKIP_IDEA;
      if (card.content.status === 'producing' || card.content.status === 'waiting') return wording.BULK_SKIP_RUNNING;
      return card.content.archivedAt === null ? null : wording.BULK_SKIP_ARCHIVED;
    },
    run: (card) => (card.type === 'content' ? setContentArchived(card.content.id, true) : Promise.resolve()),
  },
];

/** Whether the bar enables the action: at least one selected card qualifies. */
export function applies(action: BulkAction, cards: readonly BoardCard[]): boolean {
  return cards.some((card) => action.skip(card) === null);
}

/** What one run of an action did, for its single toast. */
export interface BulkOutcome {
  readonly done: number;
  readonly skipped: number;
  /** "“title”: why", for the first card left out. */
  readonly firstReason: string | null;
  /** Approve ideas beyond {@link APPROVE_IDEAS_MAX}: neither done nor skipped, still selected. */
  readonly left: readonly string[];
}

/**
 * Runs the action on the selected cards in board order, one at a time, each
 * call awaited before the next. `busy` says a card already has work running
 * (it is skipped); `around` wraps each call, to mark the card running.
 */
export async function runBulk(
  action: BulkAction,
  cards: readonly BoardCard[],
  input: BulkInput,
  options: {
    readonly busy: (card: BoardCard) => boolean;
    readonly around: (card: BoardCard, work: () => Promise<unknown>) => Promise<unknown>;
  },
): Promise<BulkOutcome> {
  let done = 0;
  let skipped = 0;
  let firstReason: string | null = null;
  let taken = 0;
  const left: string[] = [];
  const skip = (card: BoardCard, reason: string): void => {
    skipped += 1;
    firstReason ??= wording.bulkReason(cardTitle(card), reason);
  };
  for (const card of cards) {
    const reason = action.skip(card);
    if (reason !== null) {
      skip(card, reason);
      continue;
    }
    if (action.id === 'approve' && taken >= APPROVE_IDEAS_MAX) {
      left.push(card.cardId);
      continue;
    }
    if (options.busy(card)) {
      skip(card, wording.BULK_SKIP_BUSY);
      continue;
    }
    taken += 1;
    try {
      await options.around(card, () => action.run(card, input));
      done += 1;
    } catch (cause: unknown) {
      skip(card, describeError(cause));
    }
  }
  return { done, skipped, firstReason, left };
}

/** The run's one toast: "3 done, 1 skipped. “x”: why. 2 ideas were left selected…". */
export function bulkSummary(outcome: BulkOutcome, action?: BulkAction): string {
  // A bulk reject says how many it rejected, and that their ads are paused
  // afterwards: a pause that fails shows on that content's page, not here.
  const rejecting = action?.id === 'reject';
  const parts = [
    rejecting ? wording.bulkRejected(outcome.done, outcome.skipped) : wording.bulkDone(outcome.done, outcome.skipped),
  ];
  if (rejecting && outcome.done > 0) parts.push(wording.bulkRejectedAds(outcome.done));
  if (outcome.firstReason !== null) parts.push(outcome.firstReason);
  if (outcome.left.length > 0) parts.push(wording.bulkLeftSelected(outcome.left.length, APPROVE_IDEAS_MAX));
  return parts.join(' ');
}
